import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";

const monthLabel = (value) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const index = Number(value) - 1;
  return months[index] || "N/A";
};

export default function OffseasonProgramConfirm({ userId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = searchParams.get("programId");
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(programId || "");
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      if (!userId) return;
      const { data, error } = await supabase
        .from("user_program_follows")
        .select("program_id, programs(name, sport)")
        .eq("user_id", userId)
        .order("order_index", { ascending: true });

      if (!error) {
        setPrograms(data || []);
        if (!selectedProgramId && data?.length > 0) {
          setSelectedProgramId(data[0].program_id);
        }
      }
    };

    fetchPrograms();
  }, [userId, selectedProgramId]);

  useEffect(() => {
    const fetchProgram = async () => {
      if (!selectedProgramId) return;
      const { data, error } = await supabase
        .from("programs")
        .select("id, name, sport, off_season_start_month, off_season_end_month")
        .eq("id", selectedProgramId)
        .maybeSingle();

      if (!error) setProgram(data || null);
    };

    fetchProgram();
  }, [selectedProgramId]);

  const handleCheckout = async () => {
    if (!selectedProgramId) return;
    setLoading(true);

    const productId = "prod_U7yNx1Ccg5Dl71";
    const successUrl = `${window.location.origin}/skills-workouts?status=success`;
    const cancelUrl = `${window.location.origin}/athlete/offseason-program?programId=${selectedProgramId}&status=cancel`;

    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;

      const { data, error } = await supabase.functions.invoke(
        "athlete-development-checkout",
        {
          body: {
            productId,
            programId: selectedProgramId,
            athleteId: user?.id || userId,
            customerEmail: user?.email,
            successUrl,
            cancelUrl,
          },
        }
      );

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Development program checkout error:", err);
      alert(err.message || "Unable to start checkout.");
    } finally {
      setLoading(false);
    }
  };

  const offseasonLabel = program
    ? `${monthLabel(program.off_season_start_month)} - ${monthLabel(program.off_season_end_month)}`
    : "offseason window";

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Offseason Development</p>
            <h1 className="text-3xl font-black text-slate-900 mt-2">Get Stronger, Faster, and More Skilled — Without Guesswork</h1>
            <p className="text-sm text-slate-600 mt-3 max-w-xl">
              AI-generated workouts combine strength, conditioning, and skill development tied to your program’s expectations.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCheckout}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? "Starting checkout..." : "Start My Offseason Plan"}
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 bg-slate-100 text-slate-700 text-sm font-bold rounded-xl hover:bg-slate-200 transition"
              >
                Back
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-3">Price and payment details are shown at checkout.</p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-5 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Selected Program</p>
                <p className="text-xs text-slate-400 mt-1">This plan is tied to one program.</p>
              </div>
              <span className="text-[10px] font-bold uppercase bg-slate-900 text-white px-2 py-1 rounded-full">Required</span>
            </div>

            <select
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 text-sm rounded-lg p-3 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {programs.map((p) => (
                <option key={p.program_id} value={p.program_id}>
                  {p.programs?.name || "Program"}
                </option>
              ))}
            </select>

            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500 uppercase font-semibold">Program Details</p>
              <p className="text-base font-semibold text-slate-900 mt-2">{program?.name || "Program"}</p>
              {program?.sport && <p className="text-xs text-slate-500">{program.sport}</p>}
              <p className="text-xs text-slate-500 mt-2">Offseason window: {offseasonLabel}</p>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Free workouts are available during the offseason window for this program.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
        <h2 className="text-xl font-bold text-slate-900">Everything You Need in One Program</h2>
        <p className="text-sm text-slate-600 mt-2">
          This plan gives you a single offseason track built around your program and your data.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            "AI-powered workout generator tailored to your scores",
            "Strength & conditioning workouts",
            "Skill workouts for your sport",
            "Offseason window access",
            "Progress tracking inside Nuvio",
            "Unlimited workout generation during offseason",
          ].map((item) => (
            <div key={item} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
              {item}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
        <h2 className="text-xl font-bold text-slate-900">How It Works</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            {
              title: "1. Confirm your program",
              body: "Pick the program you want to train for and lock your offseason window.",
            },
            {
              title: "2. Generate your plan",
              body: "We combine skill and conditioning requirements into a single offseason track.",
            },
            {
              title: "3. Train and track",
              body: "Generate workouts on demand and monitor your progress inside Nuvio.",
            },
          ].map((step) => (
            <div key={step.title} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-slate-900">{step.title}</p>
              <p className="text-xs text-slate-600 mt-2">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8">
        <h2 className="text-xl font-bold text-slate-900">FAQ</h2>
        <div className="mt-4 space-y-3">
          {[
            {
              q: "Do I need to pick a program?",
              a: "Yes. This plan is tied to one program so your offseason training stays aligned with that program’s expectations.",
            },
            {
              q: "When can I generate free workouts?",
              a: "Free workouts are available during the selected program’s offseason window.",
            },
            {
              q: "What if I switch programs?",
              a: "You can purchase a new offseason plan for another program when needed.",
            },
          ].map((item, index) => {
            const open = expandedFaq === index;
            return (
              <button
                key={item.q}
                type="button"
                onClick={() => setExpandedFaq(open ? null : index)}
                className={`w-full text-left rounded-2xl border p-4 transition ${
                  open ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{item.q}</p>
                  <span className="text-xs font-bold text-blue-600">{open ? "−" : "+"}</span>
                </div>
                {open && <p className="text-xs text-slate-600 mt-2">{item.a}</p>}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Start Your Offseason Program</h2>
            <p className="text-sm text-blue-100 mt-2">AI-powered strength, conditioning, and skills built for you and your program.</p>
          </div>
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="bg-white text-blue-700 font-bold px-6 py-3 rounded-xl hover:bg-blue-50 transition disabled:opacity-80"
          >
            {loading ? "Starting checkout..." : "Start My Offseason Plan"}
          </button>
        </div>
      </section>
    </div>
  );
}
